/* ============================================================
   COVER — the masking-transition lifecycle, as PURE intent.
   ------------------------------------------------------------
   A channel bumper is the visible consequence of a TUNE EVENT —
   "I switched channels" — NOT an emergent property of whether the
   foreground video happens to be buffered/on-target. Deriving it
   from a tune-intent timeline (when did we tune, for how long)
   makes the bad states unrepresentable: no-bump-on-return-to-
   source, stale/muted reveal, restart-on-mash. The reconciler
   stays purely about content convergence; this owns the cover.
   ============================================================ */

/** A MASKING cover over the picture (null = none). An ad is its own full-screen content. */
export type CoverKind = 'channel' | 'ad' | null

/** A channel tune in flight: stamped at every channel LANDING (landLive/landChannelManual),
 *  null once settled. `at` is the wall-clock start, `holdMs` the bump duration (the chosen
 *  scene's exact derived length — see getSceneDurationMs). `scene`/`params` are the bump RESOLVED
 *  ONCE at stamp time and carried here, so the hold and the rendered cover are the same scene
 *  (resolveBreak randomizes, so re-resolving at render time could pick a different length). */
export interface TuneIntent {
  at: number
  holdMs: number
  targetIndex: number
  scene: string
  params: Record<string, unknown>
}

/** Liveness backstop: a frame that never reaches on-target (stuck / buffering / dead) must
 *  still REVEAL after this much past the minimum hold, so the cover can't trap forever
 *  (permanent muted snow). The cover terminates on its OWN clock, not the player's. */
export const REVEAL_GRACE_MS = 8000

/** Pure cover lifecycle. Given the tune intent, the desired content kind, whether the target
 *  is genuinely up (`onTarget`), and `now`, return the next tune state (null once landed) and
 *  the cover to show this tick. A channel cover masks BOTH a video and an ad target while
 *  tuning; the ad becomes its own ('ad') cover only after the tune lands. */
export const deriveCover = (
  tune: TuneIntent | null,
  desiredKind: 'off' | 'video' | 'scene' | 'component',
  onTarget: boolean,
  now: number,
): { tune: TuneIntent | null; cover: CoverKind } => {
  // Any full-screen non-video content (a 3D scene OR a React component) "lands" as its own content
  // cover the moment it's up — neither reports on-target, both just become the picture.
  const adCover: CoverKind = desiredKind === 'scene' || desiredKind === 'component' ? 'ad' : null
  if (!tune) return { tune: null, cover: adCover }
  const elapsed = now - tune.at
  // "Landed" = the target content is up. A video lands on-target; an ad/off "lands" as soon
  // as it's the content (an ad never reports on-target). The grace clause guarantees a reveal
  // even if the picture never arrives.
  const landed =
    desiredKind !== 'video' || onTarget || elapsed >= tune.holdMs + REVEAL_GRACE_MS
  const tuning = !(elapsed >= tune.holdMs && landed)
  return {
    tune: tuning ? tune : null,
    cover: tuning ? 'channel' : adCover,
  }
}
